/*
================================================================================
| FILENAME: src/features/insights/InsightsView.jsx (Updated for Custom Icons)
| PURPOSE:  To apply specific branding (colors and icons) for each product
|           category in the Competitor News feed.
|
| KEY CHANGES:
| 1.  Imported new icons from `lucide-react` as requested (Landmark, Group,
|     Workflow, Cloud, Sheet, Code).
| 2.  Expanded the `categoryStyles` object to include specific entries for each
|     of your products, mapping them to the desired colors and new icons.
| 3.  Added a hidden div with all necessary Tailwind CSS classes to ensure
|     the dynamic styles are included in the final production build.
| 4.  Corrected the import path for `apiService` to resolve the build error.
================================================================================
*/
import React, { useState, useEffect, useCallback } from 'react';
import {
    Pin, PinOff, RefreshCw, Loader, AlertTriangle, Users, Lightbulb, Database, Link, BarChartHorizontal, ExternalLink,
    Landmark, Group, Workflow, Cloud, Sheet, Code // New icons for custom product styles
} from 'lucide-react';
import apiService from '../../services/apiService';

// --- STYLING & CONFIGURATION ---
const categoryStyles = {
    // --- NEW: Custom styles for your specific products ---
    "SplashBI for Financial Analytics": { color: "emerald", icon: <Landmark size={14} /> },
    "SplashBI for People Analytics": { color: "purple", icon: <Group size={14} /> },
    "Data Pipeline": { color: "orange", icon: <Workflow size={14} /> },
    "SplashBI for Oracle Fusion Cloud": { color: "red", icon: <Cloud size={14} /> },
    "GL Connect": { color: "green", icon: <Sheet size={14} /> },
    "SQL Connect": { color: "sky", icon: <Code size={14} /> },

    // --- Original default styles ---
    "People Analytics": { color: "sky", icon: <Users size={14} /> },
    "default": { color: "slate", icon: <BarChartHorizontal size={14} /> }
};

const getCategoryClasses = (category) => {
    const style = categoryStyles[category] || categoryStyles.default;
    return { textColor: `text-${style.color}-400`, bgColor: `bg-${style.color}-500/10` };
};

const useInsightsWithPinning = (fetchFunction) => {
    const [allItems, setAllItems] = useState([]);
    const [pinnedItems, setPinnedItems] = useState(new Set());
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadData = useCallback(async (forceRefresh = false) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await fetchFunction(15, forceRefresh);
            const key = Object.keys(data)[0];
            const newItemsFromServer = data[key] || [];
            const itemsWithIds = newItemsFromServer.map(item => ({...item, id: item._id || item.title }));
            if (forceRefresh) {
                setAllItems(currentItems => {
                    const pinned = currentItems.filter(item => pinnedItems.has(item.id));
                    const newUnpinned = itemsWithIds.filter(newItem => !pinnedItems.has(newItem.id));
                    const uniqueNew = newUnpinned.filter(nu => !pinned.find(p => p.id === nu.id));
                    return [...pinned, ...uniqueNew];
                });
            } else {
                setAllItems(itemsWithIds);
            }
        } catch (err) {
            setError('Failed to load insights. Please try again.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, [fetchFunction, pinnedItems]);

    useEffect(() => { loadData(false); }, [loadData]);

    const togglePin = (itemId) => {
        const newPinned = new Set(pinnedItems);
        if (newPinned.has(itemId)) { newPinned.delete(itemId); } else { newPinned.add(itemId); }
        setPinnedItems(newPinned);
        setAllItems(currentItems => [...currentItems].sort((a, b) => {
            const aIsPinned = newPinned.has(a.id);
            const bIsPinned = newPinned.has(b.id);
            if (aIsPinned && !bIsPinned) return -1;
            if (!aIsPinned && bIsPinned) return 1;
            return 0;
        }));
    };

    const refresh = () => loadData(true);
    return { allItems, pinnedItems, isLoading, error, refresh, togglePin };
};

const InsightCard = ({ item, isPinned, onPin, type }) => {
    const baseCardClasses = "bg-slate-800/50 border rounded-lg p-4 flex flex-col gap-3 transition-all duration-300 break-inside-avoid";

    if (type === 'competitor') {
        const { textColor, bgColor } = getCategoryClasses(item.category);
        const style = categoryStyles[item.category] || categoryStyles.default;
        return (
            <div className={`${baseCardClasses} ${isPinned ? 'border-brand-500/50' : 'border-slate-700'}`}>
                <div className="flex justify-between items-start">
                    <div className={`flex items-center gap-2 text-xs font-bold ${textColor}`}>
                        <span className={`p-1.5 rounded-full ${bgColor}`}>{style.icon}</span>
                        <span>{item.category}</span>
                    </div>
                    <button onClick={() => onPin(item.id)} className={`p-2 -mr-2 -mt-2 rounded-full transition-colors ${isPinned ? 'text-brand-400 bg-brand-500/10' : 'text-slate-500 hover:bg-slate-700'}`}>{isPinned ? <PinOff size={16} /> : <Pin size={16} />}</button>
                </div>
                <div>
                    <h3 className="font-semibold text-slate-100">{item.title}</h3>
                    <p className="text-sm text-slate-400 mt-1">{item.description}</p>
                </div>
                {item.sourceUrl && item.sourceUrl !== '#' && (
                     <div className="mt-auto pt-3">
                         <a href={item.sourceUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-400 hover:text-brand-300 transition-colors">
                            View Source <ExternalLink size={14} />
                        </a>
                    </div>
                )}
            </div>
        );
    }

    if (type === 'idea') {
         return (
            <div className={`${baseCardClasses} ${isPinned ? 'border-purple-500/50' : 'border-slate-700'}`}>
                <div className="flex justify-between items-start">
                    <p className="text-xs font-bold text-purple-400 bg-purple-500/10 px-2 py-1 rounded-full">{item.tag}</p>
                    <button onClick={() => onPin(item.id)} className={`p-2 -mr-2 -mt-2 rounded-full transition-colors ${isPinned ? 'text-purple-400 bg-purple-500/10' : 'text-slate-500 hover:bg-slate-700'}`}>{isPinned ? <PinOff size={16} /> : <Pin size={16} />}</button>
                </div>
                <div>
                    <h3 className="font-semibold text-slate-100">{item.title}</h3>
                    <p className="text-sm text-slate-400 mt-1">{item.description}</p>
                </div>
            </div>
        );
    }

    return null;
};

const InsightsTabPanel = ({ fetchFunction, type }) => {
    const { allItems, pinnedItems, isLoading, error, refresh, togglePin } = useInsightsWithPinning(fetchFunction);
    return (
        <div>
            <div className="flex justify-end mb-6">
                 <button onClick={refresh} className="flex items-center gap-2 bg-slate-800 border border-slate-700 text-slate-100 font-semibold text-sm py-2 px-4 rounded-md transition-all duration-300 disabled:opacity-50 hover:border-slate-500" disabled={isLoading}>
                    <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
                    {isLoading ? 'Refreshing...' : 'Get New Insights'}
                </button>
            </div>
             {isLoading && allItems.length === 0 && <div className="text-center text-slate-400 py-10"><Loader className="animate-spin inline-block" /></div>}
             {error && <div className="text-center text-red-400 py-10"><AlertTriangle className="inline-block mr-2" /> {error}</div>}
             <div className="columns-1 md:columns-2 xl:columns-3 2xl:columns-4 gap-4 space-y-4">
                {allItems.map(item => (<InsightCard key={item.id} item={item} isPinned={pinnedItems.has(item.id)} onPin={togglePin} type={type} />))}
             </div>
        </div>
    );
};

export default function InsightsView({ activeViewPayload }) {
    const [activeTab, setActiveTab] = useState('competitor');

    useEffect(() => {
        if (activeViewPayload?.tab) {
            setActiveTab(activeViewPayload.tab);
        }
    }, [activeViewPayload]);

    return (
        <div className="p-4 sm:p-6 md:p-8 animate-fade-in min-h-full">
            {/* This hidden div ensures Tailwind's JIT compiler includes the dynamic classes we need for the custom product styles */}
            <div className="hidden text-emerald-400 bg-emerald-500/10 text-purple-400 bg-purple-500/10 text-orange-400 bg-orange-500/10 text-red-400 bg-red-500/10 text-green-400 bg-green-500/10 text-sky-400 bg-sky-500/10"></div>
            
            <header className="border-b border-slate-800/50 pb-5 mb-8">
                <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400">Insights</h1>
                <p className="text-lg text-slate-400 mt-2">A deeper dive into competitor movements and campaign ideas.</p>
            </header>
            <div>
                <div className="flex border-b border-slate-700 mb-6">
                    <button onClick={() => setActiveTab('competitor')} className={`px-4 py-2 font-semibold transition-colors flex items-center gap-2 ${activeTab === 'competitor' ? 'text-brand-400 border-b-2 border-brand-400' : 'text-slate-400 hover:text-brand-400/70'}`}>
                        <Users size={16}/> Competitor News
                    </button>
                    <button onClick={() => setActiveTab('idea')} className={`px-4 py-2 font-semibold transition-colors flex items-center gap-2 ${activeTab === 'idea' ? 'text-brand-400 border-b-2 border-brand-400' : 'text-slate-400 hover:text-brand-400/70'}`}>
                        <Lightbulb size={16}/> Campaign Ideas
                    </button>
                </div>
                {activeTab === 'competitor' && <InsightsTabPanel fetchFunction={apiService.fetchCompetitorWatchFeed} type="competitor" />}
                {activeTab === 'idea' && <InsightsTabPanel fetchFunction={apiService.fetchNewIdeas} type="idea" />}
            </div>
        </div>
    );
};
